import NoImageSrc from "@/assets/images/no_image.svg";
import { FooterContent, SocialMediaIcon } from "@/common/lib/types";
import { getFieldValue, getLangFromLocale } from "@/utils/common";
// import Image from "next/image";
import { CLink } from "../common/customLink/CustomLink";
import LocalSelector from "../localeSelector/LocaleSelector";
import Accordion from "../ui/accordion";
import Newsletter from "./components/Newslettr";
import Image from "@/components/ui/imageV2/ImageV2";

export interface IProps {
  footerContent: FooterContent;
  socialMediaIcons: SocialMediaIcon[];
  translations?: Record<string, string>;
  locale: string;
}

const Footer = ({ footerContent, socialMediaIcons, translations, locale }: IProps) => {
  const lang = getLangFromLocale(locale);

  return (
    <div className="container mt-16 px-4 pb-9 md:mt-24 md:px-12 lg:border-t-[0.063rem] lg:border-tertiary-3">
      <div>
        {/* Footer links */}
        <div className="grid gap-1 pb-10 md:py-10 lg:grid-cols-3">
          <div className="col-span-3 flex justify-center lg:order-2 lg:col-span-1 lg:justify-end">
            <div className="relative h-20 w-full">
              <Image
                src={footerContent?.logo || NoImageSrc}
                fill
                alt={getFieldValue(lang, "doctorm logo", "شعار الطبيب") ?? "doctorm logo"}
                className="object-contain"
              />
            </div>
          </div>
          <div className="col-span-3 mt-8 lg:hidden">
            <Accordion
              items={footerContent?.menu?.map((item) => ({
                title: item?.title || "",
                content: (
                  <ul>
                    {item?.menu?.map((mLink, index) => (
                      <li
                        key={mLink?.key || index}
                        className={`${index === 0 ? "my-5" : "mb-5"} text-sm capitalize`}
                      >
                        <CLink href={mLink?.link?.url || ""}>{mLink?.title || ""}</CLink>
                      </li>
                    ))}
                  </ul>
                ),
              }))}
            />
          </div>
          <div className="col-span-2 hidden grid-cols-4 gap-2 lg:grid">
            {footerContent?.menu?.map((item, index) => (
              <div key={item?.key || index}>
                <h3 className="mb-6 text-xl font-semibold">{item?.title || ""}</h3>
                <ul>
                  {item?.menu?.map((mLink) => (
                    <li key={mLink.key} className="mt-5 text-sm capitalize">
                      <CLink href={mLink?.link?.url || ""}>
                        <p>{mLink?.title || ""}</p>
                      </CLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        {/* Social links & newsletter */}
        <div className="mb-8 hidden h-[1px] bg-black md:block" />
        <div className="grid md:grid-cols-2">
          <div className="md:order-2">
            <h4 className="mb-8 text-center text-[1rem] font-medium leading-snug md:hidden">
              {translations?.footer_social_media_icons_title || ""}
            </h4>
            <div className="mb-11 flex justify-center gap-6 md:justify-end">
              {socialMediaIcons?.map((item, index) => (
                <a
                  target="_blank"
                  key={item?.key}
                  href={item?.url}
                  className="flex h-8 w-8 items-center justify-center"
                >
                  <Image
                    src={item?.media || ""}
                    width={60}
                    height={60}
                    alt={item?.type}
                    className="h-auto"
                  />
                </a>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-center md:justify-start">
              <h4 className="flex w-[95px] items-center justify-center text-[1rem] font-medium leading-[21px] -tracking-[0.3px] md:h-[40px] md:w-[102px] md:text-start md:text-[1.25rem] md:font-semibold md:leading-10">
                {translations?.footer_newsletter_label || ""}
              </h4>
            </div>
            <p className="mt-5 text-center text-[0.875rem] font-normal leading-snug text-tertiary-6 md:text-start md:text-[1rem] md:leading-6 md:text-black">
              {translations?.footer_newsletter_description || ""}
            </p>

            <Newsletter lang={lang} translations={translations} />
          </div>
        </div>
        <h4 className="mb-7 mt-12 text-center text-[1rem] font-medium leading-snug md:mb-4 md:text-start">
          {footerContent?.payment?.title || ""}
        </h4>
        <div className="grid grid-cols-3 place-items-center gap-2 md:flex">
          {footerContent?.payment?.items?.map((item, index) => (
            <div key={index} className={`flex items-center justify-center md:me-10`}>
              <Image
                src={item?.media ?? NoImageSrc}
                alt={item?.title || "Payment icon"}
                width={50}
                height={50}
                className="w-[64px]"
              />
            </div>
          ))}
        </div>
        {/* Region & local selector */}
        <div className="mt-6 hidden h-[1px] bg-black md:block" />
        <div className="grid grid-cols-1 py-5 md:grid-cols-2">
          <div className="flex items-center justify-center md:justify-start">
            <p className="text-[.8125rem] font-normal leading-snug text-tertiary-6 md:text-black">
              {(translations?.footer_copyright_label || "").replace(
                "{{year}}",
                `${new Date().getFullYear()}`,
              )}
            </p>
          </div>
          <div className="hidden justify-end gap-3 place-self-end md:flex">
            <LocalSelector />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Footer;
